import { ProductType } from '@pages/product-analyzer/Product';
import { PageName } from '@utils/pageConfig/Types';

/**
 * SubscribedProductType stores object related to subscribedProductTypes Redux state
 */

/**
 * getEffectiveProductType
 *
 * A user can have multiple subscribed product types, e.g. PerformanceAnalyzer & ProductsAnalyzer.
 * This utility returns the product type a page should use when calling service APIs.
 *
 * E.g. 1. A user with PerformanceAnalyzer & ProductsAnalyzer visits CategoryLeaderboard, should get PerformanceAnalyzer
 *      2. A user with PerformanceAnalyzer & ProductsAnalyzer visits Products, should get ProductsAnalyzer
 *      3. A user with Retail KAD visits RetailerOverview (should not happen but if this happens), exception will be thrown.
 *
 * @param subscribedProductTypes - the subscribed product types the user has, should be retrieved from Redux
 * @param pageName - the page the user is currently in
 * @returns the product type the page should use to call service APIs, exception will be thrown if no matching product type is found
 *
 */

export const getEffectiveProductType = (subscribedProductTypes: ProductType[], pageName: PageName): ProductType => {
  let productType: ProductType | undefined;
  switch (pageName) {
    case PageName.RETAILER_OVERVIEW:
    case PageName.OPPORTUNITY_REVIEW: {
      productType = subscribedProductTypes.find((prod) => prod === ProductType.PerformanceAnalyzer);
      break;
    }
    case PageName.EXPLORE:
    case PageName.CATEGORY_LEADERBOARD:
    case PageName.PERFORMANCE_ANALYSIS: {
      productType = subscribedProductTypes.find(
        (prod) => prod === ProductType.PerformanceAnalyzer || prod === ProductType.RetailKad,
      );
      break;
    }
    case PageName.RI_PRODUCT: {
      productType = subscribedProductTypes.find(
        (prod) =>
          prod === ProductType.ProductAnalyzer || prod === ProductType.RetailKad || prod === ProductType.SellerInsights,
      );
      break;
    }
    case PageName.TRADING_ASSISTANT: {
      productType = subscribedProductTypes.find(
        (prod) => prod === ProductType.MarketPremium || prod === ProductType.RetailKad,
      );
      break;
    }
  }

  if (!productType) {
    throw new Error('No matching product type in the subscribed product types!');
  }

  return productType;
};
